import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import AuthRoute from './AuthRoute';
import { USER_ROLE } from '@/shared/constants';

import MainLayout from '@/shared/components/layout/MainLayout';
import PageWrapper from './PageWrapper';

import type { TopBarHandle } from '@/shared/components/layout/types';

const StudentHomePage = lazy(() => import('@/pages/student/home/Page'));
const StudentAttendancePage = lazy(() => import('@/pages/student/attendance/Page'));

const StudentLessonBoardLayout = lazy(() => import('@/pages/student/lesson/board/[lessonId]/Layout'));
const StudentLessonBoardNoticeDetailPage = lazy(() => import('@/pages/student/lesson/board/[lessonId]/class-notice/[noticeId]/Page'));
const StudentLessonBoardProgressVodDetailPage = lazy(() => import('@/pages/student/lesson/board/[lessonId]/progress-vod/[vodId]/Page'));
const StudentLessonBoardQuizAssignmentDetailPage = lazy(() => import('@/pages/student/lesson/board/[lessonId]/quiz-assignment/[quizAssignmentId]/Page'));
const StudentNoticePage = lazy(() => import('@/pages/student/notice/Page'));
const StudentNoticeDetailPage = lazy(() => import('@/pages/student/notice/detail/[noticeId]/Page'));

const StudentMyClassPage = lazy(() => import('@/pages/student/my-class/Page'));
const StudentClassroomPage = lazy(() => import('@/pages/student/classroom/Page'));
const StudentAssignmentLogPage = lazy(() => import('@/pages/student/assignment/log/Page'));
const StudentAssignmentSetupPage = lazy(() => import('@/pages/student/assignment/setup/Page'));
const StudentSettingsAccountPage = lazy(() => import('@/pages/student/settings/account/Page'));
const StudentInfoEditPage = lazy(() => import('@/pages/student/settings/account/edit/Page'));
const StudentSettingsNotificationsPage = lazy(() => import('@/pages/student/settings/notifications/Page'));

/**
 * student/parent는 capacitor-gate-erp의 라우트 구성을 기준으로 삼는다.
 * 수업 게시판(lesson/board)·notice는 API가 있어 다시 연결했다. lesson/report-card/activities는
 * 학생용 API가 없는 껍데기 화면이라 계속 연결을 끊어 둔다 — 페이지 파일은 그대로 있다.
 */
export const studentRoutes: RouteObject = {
  path: '/student',
  element: <AuthRoute allowedRoles={[USER_ROLE.STUDENT]} />,
  children: [
    {
      element: <MainLayout />,
      children: [
        { index: true, element: <Navigate to='home' replace /> },
        {
          path: 'home',
          element: (
            <PageWrapper>
              <StudentHomePage />
            </PageWrapper>
          ),
        },
        {
          path: 'attendance',
          element: (
            <PageWrapper>
              <StudentAttendancePage />
            </PageWrapper>
          ),
        },
        {
          path: 'my-class',
          element: (
            <PageWrapper>
              <StudentMyClassPage />
            </PageWrapper>
          ),
        },
        {
          path: 'classroom/:lessonId',
          handle: { title: 'Classroom', showBack: true } satisfies TopBarHandle,
          element: (
            <PageWrapper>
              <StudentClassroomPage />
            </PageWrapper>
          ),
        },
        {
          path: 'lesson/board/:lessonId',
          // 목록(탭)은 Layout이 ?tab= 으로 조건부 렌더링하고, 여기엔 상세 경로만 둔다. 상단바 handle은 상세 경로도 상속한다
          handle: { title: '수업 게시판', showBack: true } satisfies TopBarHandle,
          element: (
            <PageWrapper>
              <StudentLessonBoardLayout />
            </PageWrapper>
          ),
          children: [
            {
              path: 'class-notice/:noticeId',
              element: (
                <PageWrapper>
                  <StudentLessonBoardNoticeDetailPage />
                </PageWrapper>
              ),
            },
            {
              path: 'progress-vod/:vodId',
              element: (
                <PageWrapper>
                  <StudentLessonBoardProgressVodDetailPage />
                </PageWrapper>
              ),
            },
            {
              path: 'quiz-assignment/:quizAssignmentId',
              element: (
                <PageWrapper>
                  <StudentLessonBoardQuizAssignmentDetailPage />
                </PageWrapper>
              ),
            },
          ],
        },
        {
          path: 'notice',
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <StudentNoticePage />
                </PageWrapper>
              ),
            },
            {
              path: 'detail/:noticeId',
              element: (
                <PageWrapper>
                  <StudentNoticeDetailPage />
                </PageWrapper>
              ),
            },
          ],
        },
        {
          path: 'assignment/log',
          element: (
            <PageWrapper>
              <StudentAssignmentLogPage />
            </PageWrapper>
          ),
        },
        {
          path: 'assignment/setup',
          element: (
            <PageWrapper>
              <StudentAssignmentSetupPage />
            </PageWrapper>
          ),
        },
        {
          path: 'settings/account',
          element: (
            <PageWrapper>
              <StudentSettingsAccountPage />
            </PageWrapper>
          ),
        },
        {
          path: 'settings/account/edit',
          element: (
            <PageWrapper>
              <StudentInfoEditPage />
            </PageWrapper>
          ),
        },
        {
          path: 'settings/notifications',
          element: (
            <PageWrapper>
              <StudentSettingsNotificationsPage />
            </PageWrapper>
          ),
        },
      ],
    },
  ],
};
